import React, { useState, useEffect, useRef } from "react";
import { II } from "../../components/common/Icons";
import api from "../../services/api";
import { formatDateOnly } from "../../utils/helpers";
import T from "../../assets/styles/theme";
import HrLeavePg from "./HrLeavePg";import "../../assets/styles/HR/HrAttendancePg.css";

function HrAttendancePg({ initialTab = "attendance" }) {
  const [tab, setTab] = useState(initialTab);
  const [suc, setSuc] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [leaveConfig, setLeaveConfig] = useState([]);
  const [permConfig, setPermConfig] = useState({
    max_hours_per_day: 2,
    max_permissions_per_month: 3,
    min_notice_hours: 2,
    requires_approval: true
  });

  const [attDate, setAttDate] = useState(new Date().toISOString().split("T")[0]);
  const [attRole, setAttRole] = useState("");
  const [attData, setAttData] = useState([]);
  const [attCounts, setAttCounts] = useState({ present: 0, absent: 0, wfh: 0, onLeave: 0, halfday: 0, total: 0 });
  const [attLoading, setAttLoading] = useState(false);

  const [monthVal, setMonthVal] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`);
  const [monthRole, setMonthRole] = useState("");
  const [monthlyLogs, setMonthlyLogs] = useState([]);
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthDownloading, setMonthDownloading] = useState(false);

  // Per-employee filter states
  const [empList, setEmpList] = useState([]);
  const [selEmp, setSelEmp] = useState("");
  const [empSearch, setEmpSearch] = useState("");
  const [empMonthData, setEmpMonthData] = useState(null); // { records, summary }
  const [empMonthLoading, setEmpMonthLoading] = useState(false);
  const [empMonthError, setEmpMonthError] = useState("");
  const [empDropOpen, setEmpDropOpen] = useState(false);

  // All-employees monthly summary
  const [allEmpData, setAllEmpData] = useState(null);   // { employees, working_days }
  const [allEmpLoading, setAllEmpLoading] = useState(false);
  const [allEmpError, setAllEmpError] = useState("");

  const attReqRef = useRef(0);
  const monthReqRef = useRef(0);

  const fetchAttendance = async () => {
    const reqId = ++attReqRef.current;
    setAttLoading(true);
    try {
      const d = await api.getAttendanceCounts(attDate, attRole);
      if (reqId !== attReqRef.current) return;
      if (d && d.counts) {
        setAttCounts(d.counts);
        setAttData(Array.isArray(d.records) ? d.records : []);
      } else {
        const old = await api.getAllAttendance(attDate);
        if (reqId !== attReqRef.current) return;
        setAttData(Array.isArray(old) ? old : []);
      }
    } catch (e) {
      if (reqId !== attReqRef.current) return;
      setAttData([]);
    } finally {
      if (reqId === attReqRef.current) setAttLoading(false);
    }
  };

  const fetchMonthlyLogs = async (roleArg = monthRole) => {
    const reqId = ++monthReqRef.current;
    setMonthLoading(true);
    try {
      const rows = await api.getAttendanceMonthlyLogs(roleArg);
      if (reqId !== monthReqRef.current) return;
      setMonthlyLogs(Array.isArray(rows) ? rows : []);
    } catch {
      if (reqId !== monthReqRef.current) return;
      setMonthlyLogs([]);
    } finally {
      if (reqId === monthReqRef.current) setMonthLoading(false);
    }
  };

  // Load employee list for dropdown
  useEffect(() => {
    api.getEmployeeListForAttendance().then(d => setEmpList(Array.isArray(d) ? d : [])).catch(()=>{});
  }, []);

  const fetchEmpMonthly = async () => {
    if (!selEmp || !monthVal) return;
    const [year, month] = monthVal.split("-");
    setEmpMonthLoading(true);
    setEmpMonthError("");
    setEmpMonthData(null);
    try {
      const d = await api.getEmployeeMonthlyAttendance(selEmp, Number(month), Number(year));
      // Always set data — even if records is empty, show the summary card
      setEmpMonthData({
        records: Array.isArray(d?.records) ? d.records : [],
        summary: d?.summary || {
          emp_id: selEmp, name: empSearch.split(" (")[0] || selEmp,
          department: "", month: Number(month), year: Number(year),
          total_marked: 0, present_days: 0, absent_days: 0, leave_days: 0,
          wfh_days: 0, halfday_days: 0, late_days: 0,
          total_hours: "0.00", payable_days: "0.0", lop_days: "0.0",
        }
      });
    } catch(e) {
      setEmpMonthError(e?.message || "Failed to load attendance. Please try again.");
      setEmpMonthData(null);
    }
    setEmpMonthLoading(false);
  };

  const downloadEmpMonthCsv = () => {
    if (!empMonthData?.records?.length) return;
    const s = empMonthData.summary;
    const [year, month] = monthVal.split("-");
    const monthName = new Date(year, month - 1).toLocaleString("en-IN", { month: "long" });
    const lines = [
      `# Monthly Attendance Report — ${s.name} (${s.emp_id})`,
      `# Month: ${monthName} ${year} | Dept: ${s.department}`,
      `# Working Days: ${s.working_days || "-"}`,
      ``,
      `Date,Day,Status,Check In,Check Out,Hours Worked`,
      ...empMonthData.records.map(r => {
        const d = new Date(r.date);
        const day = d.toLocaleDateString("en-IN", { weekday: "short" });
        const dt  = d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        return [dt, day, r.status||"", r.check_in||"", r.check_out||"", r.hours_worked||""].join(",");
      }),
      ``,
      `# Summary`,
      `Working Days,${s.working_days || "-"}`,
      `Total Marked Days,${s.total_marked}`,
      `Present,${s.present_days}`,
      `Absent (Weekdays),${s.absent_days}`,
      `Leave,${s.leave_days}`,
      `WFH,${s.wfh_days}`,
      `Half Day,${s.halfday_days}`,
      `Late,${s.late_days}`,
      `Days Worked,${s.days_worked}`,
      `Total Hours,${s.total_hours}`,
      `Payable Days,${s.payable_days}`,
      `LOP Days,${s.lop_days}`,
      `LWP Days,${s.lwp_days || 0}`,
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `attendance_${s.emp_id}_${year}-${month}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  // ── All employees monthly summary ──
  const fetchAllEmployeesMonthly = async () => {
    if (!monthVal) return;
    const [year, month] = monthVal.split("-");
    setAllEmpLoading(true);
    setAllEmpError("");
    try {
      const d = await api.getAllEmployeesMonthly(Number(month), Number(year), monthRole);
      setAllEmpData(d);
    } catch(e) {
      setAllEmpError(e?.message || "Failed to load employee data");
      setAllEmpData(null);
    }
    setAllEmpLoading(false);
  };

  const downloadAllEmpCsv = () => {
    if (!allEmpData?.employees?.length) return;
    const [year, month] = monthVal.split("-");
    const monthName = new Date(year, month - 1).toLocaleString("en-IN", { month: "long" });
    const header = [
      `# Monthly Attendance Summary — All Employees`,
      `# Month: ${monthName} ${year} | Working Days: ${allEmpData.working_days}`,
      ``,
      `EMP ID,Name,Role,Department,Working Days,Present,WFH,Leave,Half Day,Absent,Days Worked,LOP,LWP,Payable Days,Total Hours`,
    ];
    const rows = allEmpData.employees.map(e =>
      [e.emp_id, `"${e.name}"`, e.role, `"${e.department}"`,
       e.working_days, e.present_days, e.wfh_days, e.leave_days,
       e.halfday_days, e.absent_days, e.days_worked,
       e.lop_days, e.lwp_days, e.payable_days, e.total_hours].join(",")
    );
    // Totals row
    const tot = allEmpData.employees.reduce((acc, e) => {
      acc.present += e.present_days; acc.wfh += e.wfh_days;
      acc.leave += e.leave_days; acc.absent += e.absent_days;
      acc.worked += Number(e.days_worked); acc.lop += Number(e.lop_days);
      acc.payable += Number(e.payable_days); acc.hours += Number(e.total_hours);
      return acc;
    }, {present:0,wfh:0,leave:0,absent:0,worked:0,lop:0,payable:0,hours:0});
    rows.push([`TOTAL (${allEmpData.employees.length} employees)`,"","","","",
      tot.present, tot.wfh, tot.leave,"",tot.absent,
      tot.worked.toFixed(1), tot.lop.toFixed(1), 0, tot.payable.toFixed(1), tot.hours.toFixed(2)].join(","));
    const blob = new Blob([[...header, ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `monthly_summary_${year}-${month.padStart(2,"0")}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  useEffect(() => {
    setEmpMonthError("");
    if (selEmp && monthVal && tab === "monthly") fetchEmpMonthly();
    else setEmpMonthData(null);
  }, [selEmp, monthVal, tab]);

  // Auto-load all-employees when no specific employee selected
  useEffect(() => {
    if (!selEmp && monthVal && tab === "monthly") fetchAllEmployeesMonthly();
    else if (selEmp) setAllEmpData(null);
  }, [selEmp, monthVal, monthRole, tab]);

  useEffect(() => {
    setTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (tab === "perms") api.getAllPermissions().then(setPermissions).catch(() => {});
    if (tab === "config") api.getLeaveConfig().then((data) => {
      if (Array.isArray(data) && data.length) setLeaveConfig(data);
    }).catch(() => {});
    if (tab === "permconfig") {
      fetch("/api/hr/permission-config", { headers: { Authorization: `Bearer ${api.getToken()}` } }).
      then((r) => r.json()).then((d) => {if (d && d.id) setPermConfig(d);}).catch(() => {});
    }
    if (tab === "attendance") fetchAttendance();
    if (tab === "monthly") fetchMonthlyLogs(monthRole);
  }, [tab]);

  useEffect(() => {
    if (tab === "attendance") fetchAttendance();
  }, [attDate, attRole]);

  useEffect(() => {
    if (tab === "monthly") fetchMonthlyLogs(monthRole);
  }, [monthRole, tab]);

  const downloadCsv = () => {
    if (!attData.length) return;
    const hdr = "emp_id,name,date,status,check_in,check_out,hours_worked";
    const rows = attData.map((r) => [r.emp_id || "", r.name || "", r.date || attDate, r.status || "", r.check_in || "", r.check_out || "", r.hours_worked || ""].join(","));
    const csv = [hdr, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${attDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadMonthly = async (ym) => {
    const val = ym || monthVal;
    if (!val || !val.includes("-")) return;
    const [year, month] = val.split("-");
    try {
      setMonthDownloading(true);
      const blob = await api.downloadAttendanceMonthlyCsv(Number(month), Number(year), monthRole || "");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance-payroll-${year}-${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setSuc(`Monthly attendance downloaded for ${year}-${month}`);
      setTimeout(() => setSuc(""), 5000);
    } catch (e) {
      setSuc(`Download failed: ${e.message}`);
      setTimeout(() => setSuc(""), 5000);
    } finally {
      setMonthDownloading(false);
    }
  };

  const approvePermission = async (reqId) => {
    try {
      await api.hrPermissionAction(reqId, "approved");
      setPermissions((prev) => prev.map((p) => p.req_id === reqId ? { ...p, status: "approved", hr_status: "approved" } : p));
      setSuc("Permission approved by HR (final)");
      setTimeout(() => setSuc(""), 4000);
    } catch (e) {
      setSuc(`Error: ${e.message}`);
      setTimeout(() => setSuc(""), 5000);
    }
  };

  const rejectPermission = async (reqId) => {
    try {
      await api.hrPermissionAction(reqId, "rejected");
      setPermissions((prev) => prev.map((p) => p.req_id === reqId ? { ...p, status: "rejected", hr_status: "rejected" } : p));
      setSuc("Permission rejected by HR");
      setTimeout(() => setSuc(""), 4000);
    } catch (e) {
      setSuc(`Error: ${e.message}`);
      setTimeout(() => setSuc(""), 5000);
    }
  };

  const sChip = (s) => {
    const m = { present: "chip-ok", absent: "chip-dn", leave: "chip-wn", holiday: "chip-ip", halfday: "chip-wn", wfh: "chip-ip", late: "chip-wn" };
    return m[s] || "chip-wn";
  };

  return (
    <div>
      <div className="sec">Attendance Management</div>
      {suc && <div className="toast">{II("chk", 16, { c: "#2563eb" })} {suc}</div>}
      <div className="tabs">
        <div className={"tab" + (tab === "attendance" ? " on" : "")} onClick={() => setTab("attendance")}>Daily Attendance</div>
        <div className={"tab" + (tab === "monthly" ? " on" : "")} onClick={() => setTab("monthly")}>Monthly Download</div>
        <div className={"tab" + (tab === "config" ? " on" : "")} onClick={() => setTab("config")}>Leave Config</div>
        <div className={"tab" + (tab === "perms" ? " on" : "")} onClick={() => setTab("perms")}>Permissions</div>
        <div className={"tab" + (tab === "leaves" ? " on" : "")} onClick={() => setTab("leaves")}>Leave Management</div>
        <div className={"tab" + (tab === "permconfig" ? " on" : "")} onClick={() => setTab("permconfig")}>Permission Config</div>
      </div>

      {tab === "attendance" &&
      <div className="cd">
          <div className="cd-h">
            <div className="cd-t">Daily Attendance</div>
            <div className="hrattendancepg-inline-1">
              <input type="date" value={attDate} onChange={(e) => setAttDate(e.target.value)} style={{ "--hrattendancepg-1-border": `1px solid ${T.border}` }} className="hrattendancepg-dyn-1" />
              <select value={attRole} onChange={(e) => setAttRole(e.target.value)} style={{ "--hrattendancepg-2-border": `1px solid ${T.border}` }} className="hrattendancepg-dyn-2">
                <option value="">All Roles</option>
                <option value="employee">Employee</option>
                <option value="intern">Intern</option>
              </select>
              <button className="btn-o btn-s" onClick={fetchAttendance} disabled={attLoading}>{attLoading ? "..." : "Refresh"}</button>
              <button className="btn-o btn-s" onClick={downloadCsv} disabled={!attData.length}>{II("download", 12)} CSV</button>
            </div>
          </div>
          <div className="hrattendancepg-inline-2">
            {[["Present", attCounts.present, "#2563eb"], ["Absent", attCounts.absent, "#ef4444"], ["WFH", attCounts.wfh, "#7c3aed"], ["On Leave", attCounts.onLeave, "#f59e0b"], ["Total", attCounts.total, "#4338ca"]].map(([label, val, color], i) =>
          <div key={i} style={{ "--hrattendancepg-3-background": `${color}10`, "--hrattendancepg-3-border": `1px solid ${color}30` }} className="hrattendancepg-dyn-3">
                <div className="hrattendancepg-dyn-value" style={{ "--hrattendancepg-value-color": color }}>{val}</div>
                <div style={{ "--hrattendancepg-4-color": T.textMuted }} className="hrattendancepg-dyn-4">{label}</div>
              </div>
          )}
          </div>
          <div className="cd-b hrattendancepg-inline-3">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Emp ID</th><th>Name</th><th>Role</th><th>Dept</th><th>Status</th><th>Check In</th><th>Check Out</th><th>Hours</th>
                </tr>
              </thead>
              <tbody>
                {attLoading ?
              <tr><td colSpan="8" style={{ "--hrattendancepg-5-color": T.textMuted }} className="hrattendancepg-dyn-5">Loading...</td></tr> :
              attData.length === 0 ?
              <tr><td colSpan="8" style={{ "--hrattendancepg-6-color": T.textMuted }} className="hrattendancepg-dyn-6">No attendance for {attDate}</td></tr> :
              attData.map((a, i) =>
              <tr key={i}>
                    <td className="hrattendancepg-inline-4">{a.emp_id || "-"}</td>
                    <td className="hrattendancepg-inline-5">{a.name || "-"}</td>
                    <td className="hrattendancepg-inline-6">{a.user_role || "-"}</td>
                    <td className="hrattendancepg-inline-7">{a.department || "-"}</td>
                    <td><span className={"chip " + sChip(a.status)}>{a.status || "-"}</span></td>
                    <td className="hrattendancepg-inline-8">{a.check_in || "-"}</td>
                    <td className="hrattendancepg-inline-9">{a.check_out || "-"}</td>
                    <td>{a.hours_worked ? `${Number(a.hours_worked).toFixed(1)}h` : "-"}</td>
                  </tr>
              )}
              </tbody>
            </table>
          </div>
        </div>
      }

      {tab === "monthly" && (
        <div>
          {/* ── Controls Row ── */}
          <div className="cd" style={{marginBottom:16}}>
            <div className="cd-h">
              <div className="cd-t">Monthly Attendance</div>
              <div className="cd-s">All employees summary + per-employee day-by-day view</div>
            </div>
            <div className="cd-b">
              <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center",marginBottom:16}}>
                {/* Month picker */}
                <input type="month" value={monthVal} onChange={e => { setMonthVal(e.target.value); setEmpMonthData(null); }}
                  className="fi" style={{width:160}} />

                {/* Role filter (for bulk download) */}
                <select value={monthRole} onChange={e => setMonthRole(e.target.value)} className="fi" style={{width:140}}>
                  <option value="">All Roles</option>
                  <option value="employee">Employee</option>
                  <option value="intern">Intern</option>
                </select>

                {/* Bulk download */}
                <button className="btn" onClick={() => downloadMonthly()} disabled={monthDownloading || !monthVal}>
                  {II("download", 14)} {monthDownloading ? "Downloading..." : "Download All CSV"}
                </button>
                <button className="btn-o" onClick={() => fetchMonthlyLogs(monthRole)} disabled={monthLoading}>
                  {monthLoading ? "Refreshing..." : "Refresh Logs"}
                </button>
              </div>

              {/* ── Per-Employee Filter ── */}
              <div style={{background:"#f8fafc",border:"1.5px solid #e2e8f0",borderRadius:10,padding:"14px 16px"}}>
                <div style={{fontWeight:700,fontSize:13,color:"#1e293b",marginBottom:10}}>
                  🔍 Filter by Employee
                </div>
                <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
                  {/* Searchable employee dropdown */}
                  <div style={{position:"relative",flex:1,minWidth:220}}>
                    <input
                      placeholder="Search employee name or ID..."
                      value={empSearch}
                      onFocus={() => setEmpDropOpen(true)}
                      onChange={e => { setEmpSearch(e.target.value); setEmpDropOpen(true); setSelEmp(""); setEmpMonthData(null); }}
                      className="fi"
                      style={{width:"100%"}}
                    />
                    {empDropOpen && (
                      <div style={{position:"absolute",top:"100%",left:0,right:0,background:"#fff",border:"1px solid #e2e8f0",borderRadius:8,
                        boxShadow:"0 8px 24px rgba(0,0,0,.12)",zIndex:200,maxHeight:220,overflowY:"auto"}}>
                        <div onClick={() => { setSelEmp(""); setEmpSearch(""); setEmpDropOpen(false); setEmpMonthData(null); }}
                          style={{padding:"8px 12px",fontSize:12,color:"#94a3b8",cursor:"pointer",borderBottom:"1px solid #f1f5f9"}}>
                          — All Employees —
                        </div>
                        {empList
                          .filter(e => !empSearch || e.name.toLowerCase().includes(empSearch.toLowerCase()) || e.emp_id.toLowerCase().includes(empSearch.toLowerCase()))
                          .map(e => (
                            <div key={e.emp_id}
                              onClick={() => { setSelEmp(e.emp_id); setEmpSearch(`${e.name} (${e.emp_id})`); setEmpDropOpen(false); }}
                              style={{padding:"8px 12px",fontSize:13,cursor:"pointer",display:"flex",justifyContent:"space-between",
                                background: selEmp===e.emp_id?"#eff6ff":"#fff"}}
                              onMouseEnter={ev => ev.currentTarget.style.background="#f8fafc"}
                              onMouseLeave={ev => ev.currentTarget.style.background=selEmp===e.emp_id?"#eff6ff":"#fff"}>
                              <span style={{fontWeight:600}}>{e.name}</span>
                              <span style={{color:"#94a3b8",fontSize:11}}>{e.emp_id} · {e.role}</span>
                            </div>
                          ))}
                        {empList.filter(e => !empSearch || e.name.toLowerCase().includes(empSearch.toLowerCase()) || e.emp_id.toLowerCase().includes(empSearch.toLowerCase())).length === 0 && (
                          <div style={{padding:"10px 12px",fontSize:12,color:"#94a3b8"}}>No employees found</div>
                        )}
                      </div>
                    )}
                  </div>

                  <button className="btn" onClick={fetchEmpMonthly} disabled={!selEmp || empMonthLoading}>
                    {empMonthLoading ? "Loading..." : "View Attendance"}
                  </button>
                  {empMonthData && (
                    <button className="btn-o" onClick={downloadEmpMonthCsv}>
                      {II("download", 13)} Download CSV
                    </button>
                  )}
                </div>

                {/* Error message */}
                {empMonthError && (
                  <div style={{marginTop:10,padding:"10px 14px",background:"#fef2f2",border:"1px solid #fecaca",
                    borderRadius:7,color:"#dc2626",fontSize:13,fontWeight:600}}>
                    ❌ {empMonthError}
                  </div>
                )}

                {/* Loading spinner */}
                {empMonthLoading && (
                  <div style={{marginTop:10,padding:"10px 14px",color:"#6b7280",fontSize:13}}>
                    ⏳ Loading attendance records...
                  </div>
                )}
              </div>
            </div>

          {/* ── Per-Employee Day-by-Day View ── */}
          {empMonthData && (
            <div className="cd" style={{marginBottom:16}}>
              <div className="cd-h" style={{background:"linear-gradient(135deg,#1e1b4b,#4338ca)"}}>
                <div>
                  <div className="cd-t" style={{color:"#fff"}}>{empMonthData.summary.name} — {empMonthData.summary.emp_id}</div>
                  <div className="cd-s" style={{color:"#a5b4fc"}}>
                    {empMonthData.summary.department} · {new Date(monthVal).toLocaleString("en-IN",{month:"long",year:"numeric"})}
                    {empMonthData.summary.working_days ? ` · Working Days: ${empMonthData.summary.working_days}` : ""}
                    {empMonthData.summary.lop_from_hr &&
                      <span style={{marginLeft:8,background:"#ef4444",color:"#fff",fontSize:10,padding:"1px 6px",borderRadius:4,fontWeight:700}}>
                        LOP from leave approval
                      </span>
                    }
                  </div>
                </div>
                <button onClick={downloadEmpMonthCsv} style={{background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.3)",
                  borderRadius:7,color:"#fff",padding:"6px 14px",fontWeight:700,fontSize:12,cursor:"pointer"}}>
                  {II("download", 12)} Download CSV
                </button>
              </div>
              <div className="cd-b">
                {/* Summary stats row */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(90px,1fr))",gap:8,marginBottom:16}}>
                  {[
                    ["Present",   empMonthData.summary.present_days,  "#2563eb"],
                    ["Absent",    empMonthData.summary.absent_days,   "#ef4444"],
                    ["WFH",       empMonthData.summary.wfh_days,      "#7c3aed"],
                    ["Leave",     empMonthData.summary.leave_days,    "#f59e0b"],
                    ["Half Day",  empMonthData.summary.halfday_days,  "#d97706"],
                    ["Late",      empMonthData.summary.late_days,     "#dc2626"],
                    ["Hours",     empMonthData.summary.total_hours,   "#059669"],
                    ["Days Worked",empMonthData.summary.days_worked,  "#0891b2"],
                    ["Payable",   empMonthData.summary.payable_days,  "#16a34a"],
                    ["LOP",       empMonthData.summary.lop_days,      "#dc2626"],
                    ["LWP",       empMonthData.summary.lwp_days || 0, "#9333ea"],
                  ].map(([label,val,color]) => (
                    <div key={label} style={{background:`${color}10`,border:`1px solid ${color}25`,borderRadius:8,padding:"8px 10px",textAlign:"center"}}>
                      <div style={{fontWeight:800,fontSize:18,color}}>{val}</div>
                      <div style={{fontSize:10,color:"#64748b",fontWeight:600,marginTop:2}}>{label}</div>
                    </div>
                  ))}
                </div>

                {/* Day-by-day table */}
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Date</th><th>Day</th><th>Status</th><th>Check In</th><th>Check Out</th><th>Hours</th><th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {empMonthData.records.length === 0 ? (
                      <tr><td colSpan="7" style={{textAlign:"center",color:"#94a3b8",padding:20}}>No attendance records found for this month</td></tr>
                    ) : empMonthData.records.map((r, i) => {
                      const d = new Date(r.date);
                      const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                      const statusColors = {present:"#dcfce7",absent:"#fee2e2",wfh:"#ede9fe",leave:"#fef9c3",halfday:"#fff7ed",late:"#fef2f2",holiday:"#ffedd5"};
                      return (
                        <tr key={i} style={{background: isWeekend ? "#f8fafc" : (statusColors[r.status] || "#fff")}}>
                          <td style={{fontWeight:600}}>{d.toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}</td>
                          <td style={{color:"#64748b",fontSize:12}}>{d.toLocaleDateString("en-IN",{weekday:"short"})}</td>
                          <td><span className={"chip " + (r.status==="present"?"chip-ok":r.status==="absent"?"chip-dn":r.status==="wfh"?"chip-ip":r.status==="leave"?"chip-wn":"chip-wn")}>{r.status||"—"}</span></td>
                          <td style={{fontSize:12}}>{r.check_in||"—"}</td>
                          <td style={{fontSize:12}}>{r.check_out||"—"}</td>
                          <td>{r.hours_worked ? `${Number(r.hours_worked).toFixed(1)}h` : "—"}</td>
                          <td style={{fontSize:11,color:"#64748b"}}>{r.note||"—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── All Employees Monthly Summary ── */}
          {!selEmp && (
            <div className="cd">
              <div className="cd-h" style={{background:"linear-gradient(135deg,#0f172a,#1e3a5f)"}}>
                <div>
                  <div className="cd-t" style={{color:"#fff"}}>All Employees — Monthly Summary</div>
                  <div className="cd-s" style={{color:"#94a3b8"}}>
                    {new Date(monthVal).toLocaleString("en-IN",{month:"long",year:"numeric"})}
                    {allEmpData ? ` · ${allEmpData.employees.length} employees · Working Days: ${allEmpData.working_days}` : ""}
                  </div>
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center"}}>
                  <button className="btn-o btn-s" onClick={fetchAllEmployeesMonthly} disabled={allEmpLoading}
                    style={{color:"#fff",borderColor:"rgba(255,255,255,.4)",background:"rgba(255,255,255,.1)"}}>
                    {allEmpLoading ? "Loading..." : "↻ Refresh"}
                  </button>
                  {allEmpData?.employees?.length > 0 && (
                    <button className="btn" onClick={downloadAllEmpCsv}
                      style={{background:"#2563eb",border:"none"}}>
                      {II("download",13)} Download CSV
                    </button>
                  )}
                </div>
              </div>
              <div className="cd-b" style={{padding:0,overflowX:"auto"}}>
                {allEmpError && (
                  <div style={{padding:"12px 16px",color:"#dc2626",background:"#fef2f2",fontWeight:600,fontSize:13}}>
                    ❌ {allEmpError}
                  </div>
                )}
                {allEmpLoading ? (
                  <div style={{padding:24,textAlign:"center",color:"#94a3b8"}}>⏳ Loading employee data...</div>
                ) : allEmpData?.employees?.length === 0 ? (
                  <div style={{padding:24,textAlign:"center",color:"#94a3b8"}}>No attendance records found for this month</div>
                ) : allEmpData?.employees?.length > 0 ? (
                  <>
                    {/* Summary totals row */}
                    {(() => {
                      const tot = allEmpData.employees.reduce((a,e) => ({
                        present: a.present + e.present_days,
                        wfh: a.wfh + e.wfh_days,
                        leave: a.leave + e.leave_days,
                        absent: a.absent + e.absent_days,
                        worked: a.worked + Number(e.days_worked),
                        lop: a.lop + Number(e.lop_days),
                        payable: a.payable + Number(e.payable_days),
                        hours: a.hours + Number(e.total_hours),
                      }), {present:0,wfh:0,leave:0,absent:0,worked:0,lop:0,payable:0,hours:0});
                      return (
                        <div style={{display:"grid",gridTemplateColumns:"repeat(8,1fr)",gap:0,borderBottom:"2px solid #e2e8f0"}}>
                          {[
                            ["Employees", allEmpData.employees.length, "#1e40af"],
                            ["Present", tot.present, "#16a34a"],
                            ["WFH", tot.wfh, "#7c3aed"],
                            ["Leave", tot.leave, "#d97706"],
                            ["Absent", tot.absent, "#dc2626"],
                            ["Days Worked", tot.worked.toFixed(1), "#0891b2"],
                            ["Total LOP", tot.lop.toFixed(1), "#dc2626"],
                            ["Payable", tot.payable.toFixed(1), "#059669"],
                          ].map(([label,val,color]) => (
                            <div key={label} style={{padding:"12px 10px",textAlign:"center",borderRight:"1px solid #f1f5f9",background:`${color}08`}}>
                              <div style={{fontWeight:800,fontSize:20,color}}>{val}</div>
                              <div style={{fontSize:10,color:"#64748b",fontWeight:600,marginTop:2}}>{label}</div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>EMP ID</th>
                          <th>Name</th>
                          <th>Dept</th>
                          <th>Working Days</th>
                          <th style={{color:"#16a34a"}}>Present</th>
                          <th style={{color:"#7c3aed"}}>WFH</th>
                          <th style={{color:"#d97706"}}>Leave</th>
                          <th>Half Day</th>
                          <th style={{color:"#0891b2"}}>Days Worked</th>
                          <th style={{color:"#dc2626"}}>LOP <span style={{fontSize:9,fontWeight:400,color:"#94a3b8"}}>(approved)</span></th>
                          <th>LWP</th>
                          <th style={{color:"#059669"}}>Payable Days</th>
                          <th>Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allEmpData.employees.map((e, i) => (
                          <tr key={e.emp_id} style={{cursor:"pointer"}}
                            onClick={() => { setSelEmp(e.emp_id); setEmpSearch(`${e.name} (${e.emp_id})`); }}>
                            <td style={{fontWeight:700,color:"#2563eb",fontSize:12}}>{e.emp_id}</td>
                            <td style={{fontWeight:600}}>{e.name}</td>
                            <td style={{fontSize:12,color:"#64748b"}}>{e.department||"—"}</td>
                            <td style={{textAlign:"center"}}>{e.working_days}</td>
                            <td style={{textAlign:"center",color:"#16a34a",fontWeight:700}}>{e.present_days}</td>
                            <td style={{textAlign:"center",color:"#7c3aed",fontWeight:700}}>{e.wfh_days}</td>
                            <td style={{textAlign:"center",color:"#d97706",fontWeight:700}}>{e.leave_days}</td>
                            <td style={{textAlign:"center"}}>{e.halfday_days}</td>
                            <td style={{textAlign:"center",color:"#0891b2",fontWeight:700}}>{e.days_worked}</td>
                            <td style={{textAlign:"center"}}>
                              {Number(e.lop_days) > 0 ? (
                                <span style={{
                                  background:"#fee2e2", color:"#dc2626",
                                  fontWeight:700, borderRadius:5, padding:"2px 8px", fontSize:12
                                }}>{e.lop_days}</span>
                              ) : (
                                <span style={{color:"#94a3b8",fontSize:12}}>—</span>
                              )}
                            </td>
                            <td style={{textAlign:"center",color:"#64748b"}}>{e.lwp_days}</td>
                            <td style={{textAlign:"center",color:"#059669",fontWeight:700}}>{e.payable_days}</td>
                            <td style={{textAlign:"center",fontSize:12,color:"#64748b"}}>{e.total_hours}h</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{padding:"8px 16px",fontSize:11,color:"#94a3b8",borderTop:"1px solid #f1f5f9",display:"flex",justifyContent:"space-between"}}>
                      <span>💡 Click any row to view day-by-day attendance</span>
                      <span>ℹ️ LOP = Loss of Pay days from approved leave requests</span>
                    </div>
                  </>
                ) : (
                  <div style={{padding:24,textAlign:"center",color:"#94a3b8"}}>
                    Select a month and click Refresh to load employee data
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      )}
      {tab === "config" &&
      <div className="cd">
          <div className="cd-h"><div className="cd-t">Leave Type Configuration</div><div className="cd-s">{leaveConfig.length} types</div></div>
          <div className="cd-b">
            {leaveConfig.length === 0 ? <div style={{ "--hrattendancepg-9-color": T.textMuted }} className="hrattendancepg-dyn-9">Loading...</div> : leaveConfig.map((t, i) =>
          <div key={i} className="pf">
                <div className="hrattendancepg-inline-18">
                  <div style={{ "--hrattendancepg-10-background": t.color || "#6366f1" }} className="hrattendancepg-dyn-10" />
                  <div><div className="hrattendancepg-inline-19">{t.leave_type}</div><div style={{ "--hrattendancepg-11-color": T.textMuted }} className="hrattendancepg-dyn-11">{t.total_per_year} days per year</div></div>
                </div>
                <span className={"chip " + (t.is_active ? "chip-ok" : "chip-dn")}>{t.is_active ? "Active" : "Inactive"}</span>
              </div>
          )}
          </div>
        </div>
      }

      {tab === "permconfig" &&
      <div className="cd">
          <div className="cd-h"><div className="cd-t">Permission Configuration</div></div>
          <div className="cd-b">
            <div className="pf"><div><div className="hrattendancepg-inline-20">Max Hours Per Day</div><div style={{ "--hrattendancepg-12-color": T.textMuted }} className="hrattendancepg-dyn-12">Maximum permission hours allowed per day</div></div><span style={{ "--hrattendancepg-13-color": T.accent }} className="hrattendancepg-dyn-13">{permConfig.max_hours_per_day} hrs</span></div>
            <div className="pf"><div><div className="hrattendancepg-inline-21">Max Permissions Per Month</div><div style={{ "--hrattendancepg-14-color": T.textMuted }} className="hrattendancepg-dyn-14">Maximum permission requests per month</div></div><span style={{ "--hrattendancepg-15-color": T.accent }} className="hrattendancepg-dyn-15">{permConfig.max_permissions_per_month || permConfig.max_per_month || 3}</span></div>
            <div className="pf"><div><div className="hrattendancepg-inline-22">Min Notice Hours</div><div style={{ "--hrattendancepg-16-color": T.textMuted }} className="hrattendancepg-dyn-16">Minimum notice required before permission</div></div><span style={{ "--hrattendancepg-17-color": T.accent }} className="hrattendancepg-dyn-17">{permConfig.min_notice_hours || permConfig.min_hours || 2} hrs</span></div>
            <div className="pf"><div><div className="hrattendancepg-inline-23">Requires Approval</div><div style={{ "--hrattendancepg-18-color": T.textMuted }} className="hrattendancepg-dyn-18">Whether manager approval is required</div></div><span className={"chip " + (permConfig.requires_approval ? "chip-ok" : "chip-wn")}>{permConfig.requires_approval ? "Yes" : "No"}</span></div>
          </div>
        </div>
      }

      {tab === "perms" &&
      <div className="cd">
          <div className="cd-h"><div className="cd-t">Permission Requests</div><div className="cd-s">{permissions.length} total - <span className="hrattendancepg-inline-24">HR final approval (after PM)</span></div></div>
          <div className="cd-b hrattendancepg-inline-25">
            <table className="tbl">
              <thead>
                <tr><th>Employee</th><th>Role</th><th>Date</th><th>From</th><th>To</th><th>Hours</th><th>Reason</th><th>PM Status</th><th>HR Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {permissions.length === 0 ?
              <tr><td colSpan="10" style={{ "--hrattendancepg-19-color": T.textMuted }} className="hrattendancepg-dyn-19">No permission requests</td></tr> :
              permissions.map((p) =>
              <tr key={p.req_id || p.id}>
                    <td className="hrattendancepg-inline-26">{p.name || "-"}</td>
                    <td className="hrattendancepg-inline-27">{p.user_role || "-"}</td>
                    <td className="hrattendancepg-inline-28">{formatDateOnly(p.date)}</td>
                    <td className="hrattendancepg-inline-29">{p.from_time}</td>
                    <td className="hrattendancepg-inline-30">{p.to_time}</td>
                    <td>{p.hours || "-"}</td>
                    <td className="hrattendancepg-inline-31">{p.reason || "-"}</td>
                    <td><span className={"chip " + (p.pm_status === "approved" ? "chip-ok" : p.pm_status === "rejected" ? "chip-dn" : "chip-wn")}>{p.pm_status || "pending"}</span></td>
                    <td><span className={"chip " + (p.hr_status === "approved" ? "chip-ok" : p.hr_status === "rejected" ? "chip-dn" : p.hr_status === "waiting" ? "chip-ip" : "chip-wn")}>{p.hr_status || "waiting"}</span></td>
                    <td>
                      {p.pm_status === "approved" && (p.hr_status === "pending" || p.hr_status === "waiting") ?
                  <div className="hrattendancepg-inline-32">
                          <button className="btn-sm-ok" onClick={() => approvePermission(p.req_id)}>OK</button>
                          <button className="btn-sm-dn" onClick={() => rejectPermission(p.req_id)}>X</button>
                        </div> :
                  <span style={{ "--hrattendancepg-20-color": T.textMuted }} className="hrattendancepg-dyn-20">{p.status === "approved" ? "Done" : p.pm_status !== "approved" ? "Waiting PM" : "-"}</span>}
                    </td>
                  </tr>
              )}
              </tbody>
            </table>
          </div>
        </div>
      }

      {tab === "leaves" && <HrLeavePg />}
    </div>
  );
}

export default HrAttendancePg;
